import React, { useState, useEffect, useCallback } from 'react';
import { Download, Mail, Phone, ChevronDown, ChevronUp, Check, Loader } from 'lucide-react';
import { API, authAxios, downloadCv, downloadCsvFile } from './adminApi';
import { useLeadsFilters } from './useLeadsFilters';
import { LeadsFilterBar } from './LeadsFilterBar';

const FitBadge = ({ fit }) => {
  const color = fit === 'Strong Fit' ? '#10B981' : fit === 'Possible Fit' ? '#0B815A' : '#F59E0B';
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '2px', color, background: `${color}18`, border: `1px solid ${color}40` }}>
      {fit}
    </span>
  );
};

const SubmissionHeader = ({ sub }) => (
  <div style={{ flex: 1, minWidth: '200px' }}>
    <div style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{sub.full_name}</div>
    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '4px' }}>
      <a href={`mailto:${sub.email}`} data-testid="lead-email-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.8125rem', color: 'var(--brand-purple)', textDecoration: 'none' }}>
        <Mail size={13} /> {sub.email}
      </a>
      {sub.phone && (
        <a href={`tel:${sub.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.8125rem', color: 'var(--text-muted)', textDecoration: 'none' }}>
          <Phone size={13} /> {sub.phone}
        </a>
      )}
      {sub.nationality && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{sub.nationality}</span>}
    </div>
  </div>
);

const SubmissionActions = ({ sub, onToggleContacted, open, setOpen }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {sub.uploads.map(u => (
      <button
        key={u.upload_id}
        data-testid={`download-cv-${u.upload_id}`}
        onClick={() => downloadCv(u.upload_id, u.filename)}
        className="btn btn-ghost btn-sm"
        title={u.filename}
        style={{ fontSize: '0.75rem' }}
      >
        <Download size={14} /> CV
      </button>
    ))}
    <button
      data-testid={`contacted-toggle-${sub.user_id}`}
      onClick={() => onToggleContacted(sub.user_id, !sub.contacted)}
      className="btn btn-sm"
      style={{
        fontSize: '0.75rem',
        background: sub.contacted ? 'rgba(16,185,129,0.1)' : 'var(--bg-subtle)',
        border: sub.contacted ? '1px solid rgba(16,185,129,0.4)' : '1px solid var(--border-default)',
        color: sub.contacted ? '#0B815A' : 'var(--text-muted)',
      }}
    >
      {sub.contacted ? <><Check size={13} /> Contacted</> : 'Mark Contacted'}
    </button>
    <button data-testid={`expand-lead-${sub.user_id}`} onClick={() => setOpen(!open)} className="btn btn-ghost btn-sm">
      {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
    </button>
  </div>
);

const SubmissionDetails = ({ sub }) => {
  const answers = Object.entries(sub.questionnaire_answers || {});
  return (
    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-default)' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Questionnaire Answers
      </div>
      {answers.length === 0 ? (
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No questionnaire answers yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
          {answers.map(([k, v]) => (
            <div key={k} style={{ padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: '4px', fontSize: '0.8125rem' }}>
              <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}: </span>
              <strong>{String(v)}</strong>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        Signed up: {sub.created_at ? new Date(sub.created_at).toLocaleString() : '—'}
        {sub.latest_assessment && ` · Last assessment: ${new Date(sub.latest_assessment.created_at).toLocaleString()}`}
      </div>
    </div>
  );
};

const SubmissionRow = ({ sub, onToggleContacted }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`lead-row-${sub.user_id}`} className="card" style={{ marginBottom: '0.75rem', padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <SubmissionHeader sub={sub} />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {(sub.latest_assessment?.top_countries || []).map(tc => (
            <span key={tc.country} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {tc.country} <FitBadge fit={tc.fit} />
            </span>
          ))}
        </div>
        {sub.contact_consent ? (
          <span className="badge badge-success" style={{ fontSize: '0.65rem' }} data-testid={`consent-badge-${sub.user_id}`}>CONSENTED</span>
        ) : (
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '2px', color: '#8B827D', background: 'rgba(139,130,125,0.1)', border: '1px solid rgba(139,130,125,0.3)' }} data-testid={`consent-badge-${sub.user_id}`}>NO CONSENT</span>
        )}
        {sub.report_requested && (
          <span className="badge badge-warning" style={{ fontSize: '0.65rem' }}>REPORT REQUESTED</span>
        )}
        <SubmissionActions sub={sub} onToggleContacted={onToggleContacted} open={open} setOpen={setOpen} />
      </div>
      {open && <SubmissionDetails sub={sub} />}
    </div>
  );
};

const ExitLeadsCard = ({ exitLeads, onExport }) => (
  <div className="card" style={{ marginTop: '2rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '1rem', margin: 0 }}>Exit-Intent Leads ({exitLeads.length})</h3>
      <button data-testid="export-exit-leads-btn" onClick={onExport} className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem' }}>
        Export
      </button>
    </div>
    {exitLeads.length === 0 ? (
      <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1.5rem 0' }}>No captured leads yet</p>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
        {exitLeads.map((l, i) => (
          <div key={`${l.email}-${i}`} data-testid={`exit-lead-${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: '4px', fontSize: '0.8125rem' }}>
            <a href={`mailto:${l.email}`} style={{ color: 'var(--brand-purple)', textDecoration: 'none' }}>{l.email}</a>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>{l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const exportSubmissionsCsv = (rows) => {
  const out = [['Name', 'Email', 'Phone', 'Nationality', 'Top Countries', 'Consent', 'Contacted', 'Report Requested', 'Signed Up', 'Questionnaire Answers']];
  rows.forEach(s => out.push([
    s.full_name, s.email, s.phone || '', s.nationality || '',
    (s.latest_assessment?.top_countries || []).map(tc => `${tc.country} (${tc.fit})`).join('; '),
    s.contact_consent ? 'Yes' : 'No',
    s.contacted ? 'Yes' : 'No', s.report_requested ? 'Yes' : 'No',
    s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
    Object.entries(s.questionnaire_answers || {}).map(([k, v]) => `${k}=${v}`).join('; '),
  ]));
  downloadCsvFile(out, `axpats_leads_${new Date().toISOString().slice(0, 10)}.csv`);
};

const exportExitLeadsCsv = (exitLeads) => {
  const out = [['Email', 'Source', 'Captured At']];
  exitLeads.forEach(l => out.push([l.email, l.source || '', l.created_at ? new Date(l.created_at).toLocaleDateString() : '']));
  downloadCsvFile(out, `axpats_exit_leads_${new Date().toISOString().slice(0, 10)}.csv`);
};

export const LeadsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/admin/leads`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load leads', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const submissions = data?.submissions || [];
  const exitLeads = data?.exit_leads || [];
  const filters = useLeadsFilters(submissions);
  const { filtered } = filters;

  const toggleContacted = async (userId, contacted) => {
    setData(prev => ({
      ...prev,
      submissions: prev.submissions.map(s => s.user_id === userId ? { ...s, contacted } : s),
    }));
    try {
      await authAxios.post(`${API}/admin/leads/${userId}/contacted?contacted=${contacted}`);
    } catch (err) {
      console.error('Failed to update contacted status', err);
      alert('Failed to update contacted status');
      fetchLeads();
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader size={32} color="#F67425" className="animate-spin" /></div>;
  }

  return (
    <div data-testid="leads-tab">
      <LeadsFilterBar filters={filters} onExport={() => exportSubmissionsCsv(filtered)} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', margin: 0 }} data-testid="submissions-count">
          Submissions ({filtered.length}{filtered.length !== submissions.length ? ` of ${submissions.length}` : ''})
        </h3>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>CVs, questionnaire answers & contact details</span>
      </div>
      {filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', padding: '2rem 0', textAlign: 'center' }}>{submissions.length === 0 ? 'No submissions yet' : 'No submissions match your filters'}</p>
      ) : (
        filtered.map(sub => <SubmissionRow key={sub.user_id} sub={sub} onToggleContacted={toggleContacted} />)
      )}

      <ExitLeadsCard exitLeads={exitLeads} onExport={() => exportExitLeadsCsv(exitLeads)} />
    </div>
  );
};
